/* Bookstore Admin — Book editor modal.
 *
 * Split out of BookstoreAdmin.jsx (iter54) to bring that file under
 * the 1 000-LOC threshold. Handles book create/edit including cover
 * upload, Nano-Banana cover regen, OpenLibrary ISBN auto-fill, and
 * per-currency price overrides.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Save, Wand2, FileText, BookOpen } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { bookstoreAdminApi, resolveCoverUrl } from '@/lib/bookstoreApi';
import PriceOverridesEditor from './PriceOverridesEditor';
import IndieReaderTab from './IndieReaderTab';

export default function BookEditor({ book, create, onClose, onSaved }) {
  const [form, setForm] = useState(() => create
    ? {
        title: '', author: '', genre_slug: 'fiction', isbn: '',
        price_cents: 1299, page_count: 250, word_count: 70000,
        publication_date: new Date().toISOString().slice(0, 10),
        back_copy: '', sample_text: '',
        marketing_copy: '', keywords: [], status: 'draft',
        cover_front_url: '', cover_thumb_url: '',
        is_spotlight: false, is_bestseller: false,
        is_upcoming: false, in_maven_pool: true,
        // iter55g28 — Per-book visibility toggle, default visible.
        is_visible: true,
        // iter-greyrom — Reader-warning chips.
        content_tags: [],
        // Feb-2026 (indie-conv batch2) — Trope tags + heat rating.
        tropes: [],
        heat_level: 1,
      }
    : { ...book, keywords: Array.isArray(book.keywords) ? book.keywords : [],
        content_tags: Array.isArray(book.content_tags) ? book.content_tags : [],
        tropes: Array.isArray(book.tropes) ? book.tropes : [],
        heat_level: Number(book.heat_level || 1),
        is_visible: book.is_visible !== false });
  const [busy, setBusy]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [genres, setGenres]   = useState([]);
  const [keywordsText, setKeywordsText] = useState(
    (form.keywords || []).join(', '),
  );
  // iter-greyrom — Content-tag chip input. Comma-separated like keywords.
  const [contentTagsText, setContentTagsText] = useState(
    (form.content_tags || []).join(', '),
  );
  // Feb-2026 (indie-conv batch2) — Trope master list + selected tropes.
  const [tropeMaster, setTropeMaster] = useState([]);
  const [tropesText, setTropesText] = useState(
    (form.tropes || []).join(', '),
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    bookstoreAdminApi.listGenres().then((r) => setGenres(r.genres || []))
      .catch(() => {});
    bookstoreAdminApi.getTropeMaster().then((r) => setTropeMaster(r.tropes || []))
      .catch(() => {});
  }, []);

  // Toggle a trope from the master list into the comma-separated field.
  const toggleTrope = (t) => {
    const current = tropesText.split(',').map((s) => s.trim()).filter(Boolean);
    const idx = current.findIndex((s) => s.toLowerCase() === t.toLowerCase());
    if (idx >= 0) current.splice(idx, 1);
    else current.push(t);
    setTropesText(current.join(', '));
  };
  const activeTropes = new Set(
    tropesText.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean),
  );

  // iter-53b — ISBN auto-fill. Only writes into EMPTY fields so we never
  // clobber edits the admin has already made by hand. Reports back via
  // toast which fields were touched (and which were skipped).
  const autofillFromIsbn = async () => {
    const isbn = (form.isbn || '').trim();
    if (!isbn) {
      toast.error('Enter an ISBN first.');
      return;
    }
    setLookingUp(true);
    try {
      const r = await bookstoreAdminApi.lookupIsbn(isbn);
      if (!r.found) {
        toast.error(r.error === 'lookup_unavailable'
          ? 'OpenLibrary is unreachable — try again in a moment.'
          : `No OpenLibrary record found for ISBN ${r.isbn}.`);
        return;
      }
      const d = r.data || {};
      const filled = [];
      const skipped = [];
      const tryFill = (key, value) => {
        if (value == null || value === '') return;
        if (form[key]) { skipped.push(key); return; }
        set(key, value);
        filled.push(key);
      };
      tryFill('title',            d.title);
      tryFill('author',           d.author);
      tryFill('page_count',       d.page_count);
      tryFill('publication_date', (d.publication_date || '').slice(0, 10));
      if (d.cover_url && !form.cover_front_url) {
        set('cover_front_url', d.cover_url);
        filled.push('cover_front_url');
      } else if (d.cover_url) {
        skipped.push('cover_front_url');
      }
      if (filled.length === 0) {
        toast.info('Match found, but every field was already populated.');
      } else {
        toast.success(`Auto-filled ${filled.length} field${filled.length === 1 ? '' : 's'}: ${filled.join(', ')}`
          + (skipped.length ? ` (kept your edits: ${skipped.join(', ')})` : ''));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ISBN lookup failed.');
    } finally { setLookingUp(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (create) {
      toast.info('Save the book first, then upload its cover.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Cover too large (max 8 MB).');
      return;
    }
    setUploading(true);
    try {
      const r = await bookstoreAdminApi.uploadBookCover(book.id, file);
      set('cover_front_url', r.cover_url || '');
      if (r.thumb_url) set('cover_thumb_url', r.thumb_url);
      toast.success('Cover uploaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setUploading(false); }
  };

  // iter-53k — Regenerate cover via Nano-Banana using the latest metadata.
  // Save the editor first if there are unsaved changes so the regen sees
  // the up-to-date back_copy / keywords / marketing_copy.
  const [regenerating, setRegenerating] = useState(false);
  const regenerateCover = async () => {
    if (create) {
      toast.info('Save the book first, then regenerate its cover.');
      return;
    }
    setRegenerating(true);
    try {
      const r = await bookstoreAdminApi.regenerateBookCover(book.id);
      if (r.cover_front_url) set('cover_front_url', r.cover_front_url);
      if (r.cover_back_url)  set('cover_back_url',  r.cover_back_url);
      set('cover_thumb_url', '');  // backend doesn't thumbnail Nano-Banana output
      toast.success('Cover regenerated. Tip: save edits first so the AI sees the latest copy.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regeneration failed.');
    } finally { setRegenerating(false); }
  };

  // iter-romance26 — Ask Claude to author a ~700-word Chapter 1
  // opening from the book's back_copy + content_tags. Persisted on
  // `sample_text` so the Look-Inside drawer picks it up immediately.
  const [generatingSample, setGeneratingSample] = useState(false);
  const generateSample = async () => {
    if (create) {
      toast.info('Save the book first, then generate a sample chapter.');
      return;
    }
    if (form.sample_text && form.sample_text.length > 400
        && !window.confirm('This book already has a sample. Overwrite it with a fresh AI-authored one?')) {
      return;
    }
    setGeneratingSample(true);
    try {
      const r = await bookstoreAdminApi.generateBookSample(book.id);
      if (r?.sample_text) {
        set('sample_text', r.sample_text);
        toast.success(`Sample generated · ${r.chars} chars.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sample generation failed.');
    } finally { setGeneratingSample(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      // Normalise the keywords + content_tags textboxes into arrays.
      const keywords = keywordsText
        .split(',').map((t) => t.trim()).filter(Boolean);
      const content_tags = contentTagsText
        .split(',').map((t) => t.trim()).filter(Boolean);
      const tropes = tropesText
        .split(',').map((t) => t.trim().toLowerCase().replace(/\s+/g, '-')).filter(Boolean);
      const payload = { ...form, keywords, content_tags, tropes,
                          heat_level: Math.min(5, Math.max(1, Number(form.heat_level || 1))) };
      if (create) await bookstoreAdminApi.createBook(payload);
      else        await bookstoreAdminApi.updateBook(book.id, payload);
      toast.success('Saved.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const previewUrl = resolveCoverUrl(form.cover_thumb_url || form.cover_front_url);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-xl border border-border bg-card"
           data-testid="bs-admin-book-editor">
        <div className="flex items-center justify-between border-b border-border bg-panel-header/50 px-5 py-3">
          <div className="text-[13px] font-semibold">{create ? 'New book' : `Edit · ${book.title}`}</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <Tabs defaultValue="details" className="flex max-h-[calc(92vh-104px)] flex-col">
          <TabsList className="mx-5 mt-3 w-fit rounded-md border border-border bg-secondary/40 p-0.5">
            <TabsTrigger value="details" data-testid="be-tab-details"
                         className="inline-flex items-center gap-1.5 px-3 py-1 text-[12px] data-[state=active]:bg-background">
              <FileText className="h-3.5 w-3.5" /> Details
            </TabsTrigger>
            <TabsTrigger value="reader" data-testid="be-tab-reader"
                         className="inline-flex items-center gap-1.5 px-3 py-1 text-[12px] data-[state=active]:bg-background">
              <BookOpen className="h-3.5 w-3.5" /> Indie Reader
            </TabsTrigger>
          </TabsList>
          <TabsContent value="details" className="m-0 flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-3">
          {/* ---- Cover image column (spans 1 col, full height) ---- */}
          <div className="md:col-span-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Cover art</div>
            <div className="mt-1 aspect-[2/3] w-full overflow-hidden rounded-md border border-border bg-secondary"
                 data-testid="bs-admin-book-cover-preview">
              {previewUrl
                ? <img src={previewUrl} alt="cover" className="h-full w-full object-cover" />
                : <div className="flex h-full w-full items-center justify-center text-[11px] text-muted-foreground">
                    No cover yet
                  </div>}
            </div>
            <label className="mt-2 block">
              <span className="sr-only">Upload cover image</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => uploadCover(e.target.files?.[0])}
                disabled={uploading || create}
                data-testid="bs-admin-book-cover-upload"
                className="block w-full cursor-pointer rounded-md border border-border bg-background px-3 py-1.5 text-[11.5px] file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-1 file:text-[11px] file:font-semibold file:text-primary-foreground hover:file:bg-primary/90"
              />
            </label>
            {uploading && (
              <div className="mt-1 inline-flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Uploading & thumbnailing…
              </div>
            )}
            {create && (
              <div className="mt-1 text-[10.5px] text-muted-foreground">
                Save the book first to enable upload.
              </div>
            )}
            {/* iter-53k — Regenerate via Nano Banana using the latest fields. */}
            {!create && (
              <button type="button" onClick={regenerateCover}
                      disabled={regenerating || uploading}
                      data-testid="bs-admin-book-cover-regenerate"
                      className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-secondary px-3 py-1.5 text-[11.5px] font-medium hover:bg-secondary/80 disabled:opacity-50">
                {regenerating
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating with Nano Banana…</>
                  : <><Sparkles className="h-3.5 w-3.5" /> Regenerate from book metadata</>}
              </button>
            )}
            <label className="mt-3 block">
              <span className="mb-1 block text-[10.5px] text-muted-foreground">Or paste cover URL</span>
              <input value={form.cover_front_url || ''}
                     onChange={(e) => set('cover_front_url', e.target.value)}
                     data-testid="bs-admin-book-cover-url"
                     className="w-full rounded-md border border-border bg-background px-2 py-1 text-[11.5px] outline-none focus:border-primary" />
            </label>
          </div>

          {/* ---- Right side: metadata grid (spans 2 cols) ---- */}
          <div className="grid grid-cols-1 gap-3 md:col-span-2 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Title</span>
              <input value={form.title || ''} onChange={(e) => set('title', e.target.value)}
                     data-testid="bs-admin-book-title"
                     className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Author</span>
              <input value={form.author || ''} onChange={(e) => set('author', e.target.value)}
                     data-testid="bs-admin-book-author"
                     className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Genre</span>
              <select value={form.genre_slug || ''}
                      onChange={(e) => set('genre_slug', e.target.value)}
                      data-testid="bs-admin-book-genre"
                      className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary">
                {genres.length === 0 && <option value={form.genre_slug}>{form.genre_slug || 'fiction'}</option>}
                {genres.map((g) => (
                  <option key={g.slug} value={g.slug}>{g.name} ({g.slug})</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">ISBN</span>
              <div className="flex items-center gap-1.5">
                <input value={form.isbn || ''} onChange={(e) => set('isbn', e.target.value)}
                       data-testid="bs-admin-book-isbn"
                       placeholder="978-1-XXXX-XXXXX"
                       className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] font-mono-stat outline-none focus:border-primary" />
                <button type="button" onClick={autofillFromIsbn}
                        disabled={lookingUp || !form.isbn}
                        data-testid="bs-admin-book-isbn-autofill"
                        title="Auto-fill from OpenLibrary"
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5 text-[11.5px] font-medium hover:bg-secondary disabled:opacity-50">
                  {lookingUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                  Fill
                </button>
              </div>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Price (¢)</span>
              <input type="number" value={form.price_cents || 0}
                     onChange={(e) => set('price_cents', parseInt(e.target.value, 10) || 0)}
                     data-testid="bs-admin-book-price"
                     className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Publication date</span>
              <input type="date" value={(form.publication_date || '').slice(0, 10)}
                     onChange={(e) => set('publication_date', e.target.value)}
                     data-testid="bs-admin-book-pubdate"
                     className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Pages</span>
              <input type="number" value={form.page_count || 0}
                     onChange={(e) => set('page_count', parseInt(e.target.value, 10) || 0)}
                     className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Words</span>
              <input type="number" value={form.word_count || 0}
                     onChange={(e) => set('word_count', parseInt(e.target.value, 10) || 0)}
                     className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-muted-foreground">Status</span>
              <select value={form.status || 'draft'}
                      onChange={(e) => set('status', e.target.value)}
                      data-testid="bs-admin-book-status"
                      className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>
            <div className="md:col-span-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted-foreground">
                  Back-cover copy
                </span>
                <textarea value={form.back_copy || ''}
                          onChange={(e) => set('back_copy', e.target.value)}
                          rows={4}
                          data-testid="bs-admin-book-backcopy"
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary" />
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted-foreground">
                  Marketing copy <span className="text-[10px]">(shown in landing carousels & emails)</span>
                </span>
                <textarea value={form.marketing_copy || ''}
                          onChange={(e) => set('marketing_copy', e.target.value)}
                          rows={3}
                          data-testid="bs-admin-book-marketing"
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary" />
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted-foreground">
                  Keywords / tags <span className="text-[10px]">(comma-separated)</span>
                </span>
                <input value={keywordsText}
                       onChange={(e) => setKeywordsText(e.target.value)}
                       data-testid="bs-admin-book-keywords"
                       placeholder="time travel, romance, paris, debut"
                       className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
              </label>
            </div>
            {/* iter-greyrom — Reader-warning chips (comma-separated). */}
            <div className="md:col-span-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted-foreground">
                  Reader warnings <span className="text-[10px]">(comma-separated; shown as chips on the cover + detail modal — dubcon, violence, obsession, on-page smut…)</span>
                </span>
                <input value={contentTagsText}
                       onChange={(e) => setContentTagsText(e.target.value)}
                       data-testid="bs-admin-book-content-tags"
                       placeholder="mafia, obsession, dubcon, violence"
                       className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
              </label>
            </div>
            {/* Feb-2026 (indie-conv batch2) — Trope tags + heat rating. */}
            <div className="md:col-span-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted-foreground">
                  Tropes <span className="text-[10px]">(click to pick from the master list; free-tag anything else, comma-separated)</span>
                </span>
                <input value={tropesText}
                       onChange={(e) => setTropesText(e.target.value)}
                       data-testid="bs-admin-book-tropes"
                       placeholder="enemies-to-lovers, mafia, slow-burn"
                       className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
              </label>
              {tropeMaster.length > 0 && (
                <div className="mt-1.5 max-h-32 overflow-y-auto rounded-md border border-dashed border-border bg-background/50 p-2"
                     data-testid="bs-admin-book-trope-picker">
                  <div className="flex flex-wrap gap-1">
                    {tropeMaster.map((t) => {
                      const on = activeTropes.has(t.toLowerCase());
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleTrope(t)}
                          data-testid={`bs-admin-book-trope-chip-${t}`}
                          className={`rounded-full border px-2 py-[2px] text-[10px] uppercase tracking-wider transition ${
                            on
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground'
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-muted-foreground">
                  Heat level <span className="text-[10px]">
                    (1 = sweet · 2 = kisses · 3 = fade-to-black · 4 = on-page · 5 = scorching)
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={1}
                    max={5}
                    step={1}
                    value={Number(form.heat_level || 1)}
                    onChange={(e) => set('heat_level', parseInt(e.target.value, 10) || 1)}
                    data-testid="bs-admin-book-heat"
                    className="flex-1"
                  />
                  <span className="inline-flex min-w-[3rem] items-center justify-center rounded-full bg-orange-500/20 px-2 py-0.5 text-[11px] font-semibold text-orange-700 dark:text-orange-300"
                        data-testid="bs-admin-book-heat-value">
                    {'🔥'.repeat(Math.max(1, Number(form.heat_level || 1)))}
                  </span>
                </div>
              </label>
            </div>
            <div className="md:col-span-2">
              <PriceOverridesEditor
                priceCents={form.price_cents || 0}
                overrides={form.price_overrides || {}}
                onChange={(po) => set('price_overrides', po)}
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-end justify-between gap-2">
                <label className="flex-1 block">
                  <span className="mb-1 block text-[11px] text-muted-foreground">
                    Sample text <span className="text-[10px]">(shown in the Look-Inside drawer on the storefront)</span>
                  </span>
                  <textarea value={form.sample_text || ''}
                            onChange={(e) => set('sample_text', e.target.value)}
                            rows={3}
                            data-testid="bs-admin-book-sample-text"
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] outline-none focus:border-primary" />
                </label>
                {!create && (
                  <button type="button" onClick={generateSample}
                          disabled={generatingSample}
                          data-testid="bs-admin-book-sample-generate"
                          title="Ask Claude Sonnet 4.5 to author a ~700-word Chapter 1 opening from this book's back-cover copy + content tags. Persists to `sample_text` so the Look-Inside drawer picks it up immediately."
                          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-secondary px-3 py-1.5 text-[11.5px] font-medium hover:bg-secondary/80 disabled:opacity-50">
                    {generatingSample
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing…</>
                      : <><Sparkles className="h-3.5 w-3.5" /> AI-author sample</>}
                  </button>
                )}
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">
                Tip · Save your edits to back-cover copy + reader warnings first so the AI has the freshest steering.
              </div>
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-3 text-[12px]">
              {/* iter55g28 — Per-book visibility. Its own toggle
               *  outside the marketing-flag map because the gate rules
               *  differ (never disabled). */}
              <label className="inline-flex items-center gap-2"
                     title="Toggle whether customers can see this book in the storefront, genre pages, and search.">
                <input
                  type="checkbox"
                  checked={form.is_visible !== false}
                  data-testid="bs-book-editor-is_visible"
                  onChange={(e) => set('is_visible', e.target.checked)}
                />
                is visible
              </label>
              {['is_spotlight', 'is_bestseller', 'is_upcoming', 'in_maven_pool'].map((k) => {
                // iter55g27 — Spotlight / best-seller / up-and-coming
                // can only be enabled when the book's genre is Visible.
                const isMarketingFlag = k === 'is_spotlight' || k === 'is_bestseller' || k === 'is_upcoming';
                const selectedGenre = genres.find((g) => g.slug === form.genre_slug);
                const genreVisible  = selectedGenre ? (selectedGenre.enabled !== false) : true;
                const gate = isMarketingFlag && !genreVisible;
                return (
                  <label key={k}
                    className={`inline-flex items-center gap-2 ${gate ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={gate
                      ? `The "${form.genre_slug}" genre is hidden. Set it to Visible before adding this book to ${k.replace('is_', '').replace('_', '-')}.`
                      : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={!!form[k]}
                      disabled={gate}
                      data-testid={`bs-book-editor-${k}`}
                      onChange={(e) => set(k, e.target.checked)}
                    />
                    {k.replace(/_/g, ' ')}
                  </label>
                );
              })}
            </div>
          </div>
            </div>
          </TabsContent>
          <TabsContent value="reader" className="m-0 flex-1 overflow-y-auto p-5">
            <IndieReaderTab book={create ? null : book} />
          </TabsContent>
        </Tabs>
        <div className="flex justify-end gap-2 border-t border-border bg-panel-header/30 px-5 py-3">
          <button onClick={onClose} className="rounded-md px-3 py-1.5 text-[12px] hover:bg-secondary">Cancel</button>
          <button onClick={save} disabled={busy}
                  data-testid="bs-admin-book-save"
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
